import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import PageHeader from "app/main/shared-components/page-header/PageHeader";
import BaseTable from "app/main/shared-components/base-table/BaseTable";
import PaginationBar from "app/main/shared-components/pagination-bar/PaginationBar";
import CouponsTabs from "./CouponsTabs";
import { Column } from "app/types/TableTypes";
import { AppState } from "app/store";
import { fetchCouponReservations } from "./store/coupon-reservations.slice";
import { fetchPromotionalCoupons } from "./store/promotional-coupons.slice";

type ReservationCouponRow = {
  id: string;
  bookingNumber: string;
  bookingDateTime: string;
  passenger: string;
  departureFlight: string;
  returnFlight: string;
  couponCode: string;
  discount: string;
  currency: string;
};

function formatDateTime(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso as string;
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

const CouponReservationsPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const ts = (key: string, fallback: string) => String(t(key, fallback));

  const { page: reservationsPage, loading } = useSelector(
    (state: AppState) => state.coupons.reservations
  );
  const { page: couponsPage } = useSelector(
    (state: AppState) => state.coupons.promotional
  );

  useEffect(() => {
    // Load coupons (for % value) + reservations that have a coupon
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (dispatch as any)(fetchPromotionalCoupons({ page: 0, size: 20 }));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (dispatch as any)(fetchCouponReservations({ page: 0, size: 20}));
  }, [dispatch]);

  const rows: ReservationCouponRow[] = useMemo(() => {
    if (!reservationsPage || !reservationsPage.content) return [];

    const discountByCode: Record<string, number> = {};
    couponsPage?.content?.forEach((c) => {
      if (c.code) {
        discountByCode[c.code] = c.discountPercentage;
      }
    });

    return reservationsPage.content.map((r) => {
      const firstTraveler =
        r.travelers && r.travelers.length > 0 ? r.travelers[0] : undefined;

      const depFlight = firstTraveler?.departureFlight;
      const retFlight = firstTraveler?.returningFlight;

      const departureFlightLabel =
        depFlight && depFlight.departureAirport && depFlight.arrivalAirport
          ? `${depFlight.departureAirport.code}-${depFlight.arrivalAirport.code}`
          : "-";

      const returnFlightLabel =
        retFlight && retFlight.departureAirport && retFlight.arrivalAirport
          ? `${retFlight.departureAirport.code}-${retFlight.arrivalAirport.code}`
          : "-";

      const couponCode = r.appliedCouponCode ?? "";

      const discount =
        couponCode && typeof discountByCode[couponCode] === "number"
          ? `${discountByCode[couponCode]}%`
          : "-";

      const currency = r.currency?.isoCode || r.currency?.name || "";

      const bookingNumber =
        r.pnrCode ??
        (r.confirmationCode != null ? String(r.confirmationCode) : "");

      return {
        id: r.id,
        bookingNumber: bookingNumber || "",
        bookingDateTime: formatDateTime(r.createdDate),
        passenger: firstTraveler
          ? `${firstTraveler.name ?? ""} ${
              firstTraveler.surname ?? ""
            }`.trim() || "-"
          : "-",
        departureFlight: departureFlightLabel,
        returnFlight: returnFlightLabel,
        couponCode: couponCode || "-",
        discount,
        currency: currency || "",
      };
    });
  }, [reservationsPage, couponsPage]);

  const columns: Column[] = useMemo(
    () => [
      {
        Header: ts("app.main.coupons.reservations.columns.booking", "Booking #"),
        accessor: "bookingNumber",
      },
      {
        Header: ts("app.main.coupons.reservations.columns.date", "Registered"),
        accessor: "bookingDateTime",
      },
      {
        Header: ts("app.main.coupons.reservations.columns.coupon", "Coupon"),
        accessor: "couponCode",
      },
      {
        Header: ts("app.main.coupons.reservations.columns.discount", "Discount"),
        accessor: "discount",
      },
      {
        Header: ts("app.main.coupons.reservations.columns.currency", "Currency"),
        accessor: "currency",
      },
      {
        Header: ts(
          "app.main.coupons.reservations.columns.passenger",
          "Passenger"
        ),
        accessor: "passenger",
      },
      {
        Header: ts(
          "app.main.coupons.reservations.columns.departure",
          "Departure flight"
        ),
        accessor: "departureFlight",
      },
      {
        Header: ts(
          "app.main.coupons.reservations.columns.return",
          "Return flight"
        ),
        accessor: "returnFlight",
      },
    ],
    [t]
  );

  // ---- pagination like BMT Network / SalesList ----
  const pageSize = reservationsPage?.size || 50;
  const totalPages = reservationsPage?.totalPages || 1;
  const currentPageZeroBased = reservationsPage?.number ?? 0;

  return (
    <div>
      <PageHeader
        title={ts(
          "app.main.coupons.reservations.title",
          "Coupon reservations"
        )}
        desc={ts(
          "app.main.coupons.reservations.desc",
          "Reservations that were created using a promotional coupon."
        )}
      />

      <CouponsTabs />

      <div style={{ marginTop: 16 }}>
        <BaseTable columns={columns} data={rows} isLoading={loading} />

        <PaginationBar
          totalPages={totalPages}
          currentPage={currentPageZeroBased + 1}
          onPageChange={(pageZeroBased: number) => {
            const newPage = pageZeroBased;
            (dispatch as any)(
              fetchCouponReservations({
                page: newPage,
                size: pageSize,
              })
            );
          }}
        />
      </div>
    </div>
  );
};

export default CouponReservationsPage;
